import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";

import { Download, Eye, FileText, Pencil, Plus, Receipt, Search, Trash2 } from "lucide-react";

import { Button } from "../components/ui/button";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";

import { Input } from "../components/ui/input";

import { Label } from "../components/ui/label";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

import DataTable from "../components/common/DataTable";

import { erpApi, formatCurrency, formatDate, formatTime, normalizeExternalUrl } from "../services/erpApi";

import { CLINIC_NAME } from "../config/branding";

const monthLabel = (monthKey) => {
  if (!monthKey) return "All months";
  const date = new Date(`${monthKey}-01T00:00:00`);
  if (Number.isNaN(date.getTime())) return monthKey;
  return date.toLocaleDateString("en-IN", { month: "short", year: "numeric" });
};

const startOfToday = () => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date;
};

const startOfWeek = () => {
  const date = startOfToday();
  const day = date.getDay();
  const diff = day === 0 ? 6 : day - 1;
  date.setDate(date.getDate() - diff);
  return date;
};

const todayDate = () => new Date().toISOString().slice(0, 10);

const providerItemName = (name, provider) => {
  const cleanName = String(name || "").trim();
  const cleanProvider = String(provider || "").trim();
  if (!cleanProvider || cleanProvider.toLowerCase() === "clinic") return cleanName;
  if (new RegExp(`\\(${cleanProvider.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\)$`, "i").test(cleanName)) return cleanName;
  return `${cleanName} (${cleanProvider})`;
};

export default function Billing() {
  const { patientId } = useParams();

  const [patients, setPatients] = useState([]);

  const [selectedPatientId, setSelectedPatientId] = useState(patientId || "");

  const [items, setItems] = useState([]);

  const [services, setServices] = useState([]);

  const [servicesLoading, setServicesLoading] = useState(true);

  const [search, setSearch] = useState("");

  const [billSearch, setBillSearch] = useState("");

  const [billDateFilter, setBillDateFilter] = useState("all");

  const [billMonthFilter, setBillMonthFilter] = useState("all");

  const [bills, setBills] = useState([]);

  const [editingBill, setEditingBill] = useState(null);

  const [viewingBill, setViewingBill] = useState(null);

  const [item, setItem] = useState({
    name: "",
    category: "consultation",

    provider_lab: "",

    cost_price: 0,

    amount: "",

    quantity: 1,
  });

  const [discount, setDiscount] = useState(0);

  const [paidAmount, setPaidAmount] = useState("");

  const [paymentMode, setPaymentMode] = useState("cash");

  const [status, setStatus] = useState("paid");

  const [notes, setNotes] = useState("");

  const [billDate, setBillDate] = useState(todayDate());

  const [createdBill, setCreatedBill] = useState(null);

  const resetBillForm = () => {
    setItems([]);
    setDiscount(0);
    setPaidAmount("");
    setPaymentMode("cash");
    setStatus("paid");
    setNotes("");
    setBillDate(todayDate());
    setEditingBill(null);
    if (!patientId) {
      setSelectedPatientId("");
    }
  };

  const loadBills = async () => {
    try {
      const response = await erpApi.bills();
      setBills(response.data);
    } catch {
      toast.error("Failed to load invoice history");
    }
  };

  useEffect(() => {
    async function loadPatients() {
      try {
        const response = await erpApi.patients();

        setPatients(response.data);
      } catch {
        toast.error("Failed to load patients");
      }
    }

    loadPatients();
  }, []);

  useEffect(() => {
    loadBills();
  }, []);

  useEffect(() => {
    async function loadServices() {
      try {
        const response = await erpApi.services({ active: true, limit: 5000 });
        setServices(response.data);
      } catch {
        toast.error("Failed to load billing services");
      } finally {
        setServicesLoading(false);
      }
    }

    loadServices();
  }, []);

  const serviceOptions = services.flatMap((service) =>
    (service.pricing?.length ? service.pricing : [{ provider: "Clinic", cost_price: 0, selling_price: 0 }]).map((pricing, pricingIndex) => ({
      id: `${service.id}-${pricingIndex}`,
      service,
      pricing,
    })),
  );

  const filteredCatalog = serviceOptions.filter(({ service, pricing }) => {
    const term = search.toLowerCase();
    return (
      service.name.toLowerCase().includes(term) ||
      service.category.toLowerCase().includes(term) ||
      pricing.provider.toLowerCase().includes(term)
    );
  });

  const subtotal = useMemo(
    () =>
      items.reduce(
        (sum, current) =>
          sum + Number(current.amount || 0) * Number(current.quantity || 1),
        0,
      ),
    [items],
  );

  const total = Math.max(subtotal - Number(discount || 0), 0);

  const billMonthOptions = useMemo(() => {
    const months = new Set();
    bills.forEach((bill) => {
      const month = (bill.bill_date || bill.created_at || "").slice(0, 7);
      if (/^\d{4}-\d{2}$/.test(month)) {
        months.add(month);
      }
    });
    return Array.from(months).sort().reverse();
  }, [bills]);

  const filteredBills = useMemo(() => {
    const term = billSearch.trim().toLowerCase();
    const now = startOfToday();
    let startDate = null;

    if (billDateFilter === "today") {
      startDate = startOfToday();
    } else if (billDateFilter === "week") {
      startDate = startOfWeek();
    } else if (billDateFilter === "15days") {
      startDate = startOfToday();
      startDate.setDate(startDate.getDate() - 14);
    } else if (billDateFilter === "30days") {
      startDate = startOfToday();
      startDate.setDate(startDate.getDate() - 29);
    }

    return bills.filter((bill) => {
      const billDateValue = bill.bill_date || bill.created_at || "";
      const billDate = billDateValue ? new Date(billDateValue) : null;
      const billMonth = billDateValue.slice(0, 7);

      if (billMonthFilter !== "all" && billMonth !== billMonthFilter) {
        return false;
      }

      if (startDate) {
        if (!billDate || Number.isNaN(billDate.getTime())) {
          return false;
        }
        if (billDate < startDate || billDate > new Date(now.getTime() + 24 * 60 * 60 * 1000 - 1)) {
          return false;
        }
      }

      if (!term) return true;

      const searchable = [
        bill.invoice_no,
        bill.id,
        bill.patient_id,
        bill.patient_name,
        bill.patient_mobile,
        bill.bill_date,
        bill.created_at,
        bill.status,
        bill.total,
        bill.paid_amount,
        bill.due_amount,
      ]
        .filter((value) => value !== undefined && value !== null)
        .join(" ")
        .toLowerCase();

      return searchable.includes(term);
    });
  }, [billSearch, billDateFilter, billMonthFilter, bills]);

  const addItem = (nextItem = item) => {
    const quantity = Number(nextItem.quantity || 1);

    if (!nextItem.name || !nextItem.amount) {
      toast.error("Item name and amount required");
      return;
    }

    setItems([
      ...items,
      {
        ...nextItem,

        quantity,

        amount: Number(nextItem.amount),

        cost_price: Number(nextItem.cost_price || 0),

        provider_lab: nextItem.provider_lab || "",

        total: Number(nextItem.amount) * quantity,
      },
    ]);

    setItem({
      name: "",
      category: "consultation",

      provider_lab: "",

      cost_price: 0,

      amount: "",

      quantity: 1,
    });

  };

  const addCatalogItem = (catalogItem, selectedPricing) => {
    addItem({
      name: providerItemName(catalogItem.name, selectedPricing?.provider),

      category: catalogItem.category || "consultation",

      provider_lab: selectedPricing?.provider || "Clinic",

      cost_price: selectedPricing?.cost_price || 0,

      amount: selectedPricing?.selling_price || 0,

      quantity: 1,

      provider_name: selectedPricing?.provider || "Clinic",

      provider_type: catalogItem.category || "service",
    });
  };

  const submit = async () => {
    if (!selectedPatientId || items.length === 0) {
      toast.error("Select patient and add at least one item");

      return;
    }

    try {
      const payload = {
        patient_id: selectedPatientId,

        bill_date: billDate,

        items: items.map((line) => ({
          name: line.name,
          category: line.category,
          quantity: Number(line.quantity || 1),
          rate: Number(line.amount || 0),
          amount: Number(line.amount || 0),
          provider_name: line.provider_name || line.provider_lab || "",
          provider_type: line.provider_type || line.category || "",
          notes: line.notes || "",
        })),

        discount: Number(discount || 0),

        paid_amount: paidAmount === "" ? undefined : Number(paidAmount),

        payment_mode: paymentMode,

        status,

        notes,
      };

      const response = editingBill
        ? await erpApi.updateBill(editingBill.id, payload)
        : await erpApi.createBill(payload);

      setCreatedBill(response.data);

      toast.success(editingBill ? "Invoice updated" : "Invoice created");
      resetBillForm();
      loadBills();
    } catch {
      toast.error(editingBill ? "Failed to update invoice" : "Failed to create invoice");
    }
  };

  const downloadPdf = async (bill = createdBill) => {
    if (!bill) return;
    try {
      const response = await erpApi.billPdf(bill.id);
      const file = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(file);
      const link = document.createElement("a");

      link.href = url;
      const fileName = String(bill.invoice_no || bill.id.slice(0, 8)).replace(/[^a-z0-9_-]+/gi, "_");
      link.download = `invoice_${fileName}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.response?.data?.detail || "PDF download failed");
    }
  };

  const openPdfForPrint = async (bill = createdBill) => {
    if (!bill) return;
    try {
      const response = await erpApi.billPdf(bill.id);
      const file = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(file);
      const printWindow = window.open(url, "_blank", "noopener,noreferrer");
      if (!printWindow) {
        toast.error("Popup blocked. PDF button use karein.");
        window.URL.revokeObjectURL(url);
        return;
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "PDF open failed");
    }
  };

  const openImportedPdf = (bill) => {
    const pdfUrl = normalizeExternalUrl(bill.invoice_pdf_url);
    if (!pdfUrl) return toast.error("Imported PDF URL missing");
    window.open(pdfUrl, "_blank", "noopener,noreferrer");
  };

  const startEditBill = (bill) => {
    setEditingBill(bill);
    setCreatedBill(null);
    setViewingBill(null);
    setSelectedPatientId(bill.patient_id || "");
    setItems((bill.items || []).map((line) => ({
      name: line.name || "",
      category: line.category || "consultation",
      provider_lab: line.provider_name || "",
      cost_price: 0,
      amount: Number(line.rate || line.amount || 0),
      quantity: Number(line.quantity || 1),
      provider_name: line.provider_name || "",
      provider_type: line.provider_type || line.category || "",
      notes: line.notes || "",
    })));
    setDiscount(Number(bill.discount || 0));
    setPaidAmount(String(bill.paid_amount ?? ""));
    setPaymentMode(bill.payment_mode || "cash");
    setStatus(bill.status || "paid");
    setNotes(bill.notes || "");
    setBillDate((bill.bill_date || bill.created_at || "").slice(0, 10) || todayDate());
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    if (!patientId) return;

    const billFromRoute = bills.find((bill) => bill.id === patientId);
    if (billFromRoute) {
      startEditBill(billFromRoute);
      return;
    }

    setSelectedPatientId(patientId);
  }, [patientId, bills]);

  const deleteBill = async (bill) => {
    const ok = window.confirm(`Delete invoice ${bill.invoice_no || `#${bill.id.slice(0, 8).toUpperCase()}`} for ${bill.patient_name}?`);
    if (!ok) return;

    try {
      await erpApi.deleteBill(bill.id);
      toast.success("Bill deleted");
      if (createdBill?.id === bill.id) {
        setCreatedBill(null);
      }
      loadBills();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete bill");
    }
  };

  const shareWhatsApp = () => {
    const message =
      `${CLINIC_NAME}\n` +
      `Invoice: ${createdBill.invoice_no || createdBill.id?.slice(0, 8)}\n` +
      `Patient: ${createdBill.patient_name}\n` +
      `Total: ${formatCurrency(createdBill.total)}\n` +
      `Paid: ${formatCurrency(createdBill.paid_amount)}\n` +
      `Due: ${formatCurrency(createdBill.due_amount)}\n` +
      `Status: ${createdBill.status}`;

    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, "_blank");
  };

  const billColumns = [
    { key: "invoice_no", label: "Bill No.", render: (row) => row.invoice_no || `#${row.id.slice(0, 8).toUpperCase()}` },
    { key: "created_at", label: "Date", render: (row) => formatDate(row.bill_date || row.created_at) },
    { key: "created_time", label: "Time", render: (row) => formatTime(row.created_at) },
    { key: "patient_name", label: "Name", render: (row) => <Link className="font-medium text-hospital-primary" to={`/patients/${row.patient_id}`}>{row.patient_name}</Link> },
    { key: "patient_mobile", label: "Mobile" },
    { key: "patient_id", label: "Patient ID", render: (row) => row.patient_id?.slice(0, 8) || "-" },
    { key: "total", label: "Amount", align: "right", render: (row) => formatCurrency(row.total) },
    { key: "paid_amount", label: "Paid", align: "right", render: (row) => formatCurrency(row.paid_amount) },
    { key: "due_amount", label: "Due", align: "right", render: (row) => formatCurrency(row.due_amount) },
    {
      key: "actions",
      label: "",
      render: (row) => (
        <div className="flex items-center justify-end gap-1">
          <Button variant="ghost" size="icon" title="View bill" onClick={() => setViewingBill(row)}>
            <Eye className="h-4 w-4 text-slate-700" />
          </Button>
          <Button variant="ghost" size="icon" title="Edit bill" onClick={() => startEditBill(row)}>
            <Pencil className="h-4 w-4 text-hospital-primary" />
          </Button>
          {row.invoice_pdf_url && (
            <Button variant="ghost" size="icon" title="Open imported PDF" onClick={() => openImportedPdf(row)}>
              <FileText className="h-4 w-4 text-hospital-primary" />
            </Button>
          )}
          <Button variant="ghost" size="icon" title="Download PDF" onClick={() => downloadPdf(row)}>
            <Download className="h-4 w-4 text-hospital-primary" />
          </Button>
          <Button variant="ghost" size="icon" title="Delete bill" onClick={() => deleteBill(row)}>
            <Trash2 className="h-4 w-4 text-red-600" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-manrope text-2xl font-bold">Billing</h1>

        <p className="text-sm text-hospital-text-muted">
          Fast invoices with searchable treatments, lab tests, dental procedures
          and PDF receipts.
        </p>
      </div>

      {createdBill && (
      <div className="mx-auto max-w-2xl">
        <Card className="border border-gray-200">
          <CardHeader className="bg-hospital-primary text-white">
            <CardTitle>Invoice Created</CardTitle>
          </CardHeader>

          <CardContent className="space-y-4 p-6">
            <div className="flex justify-between">
              <span>Invoice No.</span>

              <strong>{createdBill.invoice_no || `#${createdBill.id.slice(0, 8).toUpperCase()}`}</strong>
            </div>

            <div className="flex justify-between">
              <span>Patient</span>

              <strong>{createdBill.patient_name}</strong>
            </div>

            <div className="flex justify-between">
              <span>Total</span>

              <strong>{formatCurrency(createdBill.total)}</strong>
            </div>

            <div className="flex justify-between">
              <span>Paid</span>

              <strong>{formatCurrency(createdBill.paid_amount)}</strong>
            </div>

            <div className="flex justify-between">
              <span>Due</span>

              <strong>{formatCurrency(createdBill.due_amount)}</strong>
            </div>

            <div className="flex justify-between">
              <span>Status</span>

              <strong className="capitalize">{createdBill.status}</strong>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <Button className="btn-primary" onClick={() => downloadPdf(createdBill)}>
                <Download className="mr-2 h-4 w-4" />
                PDF
              </Button>

              <Button variant="outline" onClick={() => openPdfForPrint(createdBill)}>
                Print
              </Button>

              <Button variant="outline" onClick={shareWhatsApp}>
                WhatsApp
              </Button>
            </div>

            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                setCreatedBill(null);
              }}
            >
              Back To Billing
            </Button>
          </CardContent>
        </Card>
      </div>
      )}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="space-y-6 xl:col-span-2">
          <Card className="border border-gray-200">
            <CardHeader>
              <CardTitle className="text-base">{editingBill ? "Edit Invoice" : "Patient & Services"}</CardTitle>
            </CardHeader>

            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Patient</Label>

                <Select
                  value={selectedPatientId}
                  onValueChange={setSelectedPatientId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select patient" />
                  </SelectTrigger>

                  <SelectContent>
                    {patients.map((patient) => (
                      <SelectItem key={patient.id} value={patient.id}>
                        {patient.name} - {patient.mobile}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <Label>Search Treatment / Test / Procedure</Label>

                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />

                  <Input
                    className="pl-10"
                    placeholder="Search RCT, CBC, X-Ray..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>

                <div className="max-h-72 overflow-y-auto rounded-xl border border-gray-200">
                  {servicesLoading ? (
                    <div className="p-4 text-sm text-gray-500">
                      Loading services...
                    </div>
                  ) : filteredCatalog.length === 0 ? (
                    <div className="p-4 text-sm text-gray-500">
                      No matching services found. Clinic admin can add services from Services.
                    </div>
                  ) : (
                    filteredCatalog.map(({ id, service, pricing }) => (
                      <button
                        key={id}
                        type="button"
                        onClick={() => addCatalogItem(service, pricing)}
                        className="flex w-full items-center justify-between border-b border-gray-100 px-4 py-3 text-left transition hover:bg-gray-50"
                      >
                        <div>
                          <div className="font-medium">{service.name}</div>

                          <div className="text-xs capitalize text-gray-500">
                            {service.category} · {pricing.provider}
                          </div>
                        </div>

                        <div className="font-semibold text-hospital-primary">
                          {formatCurrency(pricing.selling_price || 0)}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-gray-200">
            <CardHeader>
              <CardTitle className="text-base">Custom Item</CardTitle>
            </CardHeader>

            <CardContent className="grid grid-cols-1 gap-3 md:grid-cols-5">
              <Input
                placeholder="Item name"
                value={item.name}
                onChange={(e) =>
                  setItem({
                    ...item,
                    name: e.target.value,
                  })
                }
              />

              <Input
                placeholder="Category"
                value={item.category}
                onChange={(e) =>
                  setItem({
                    ...item,
                    category: e.target.value,
                  })
                }
              />

              <Input
                type="number"
                placeholder="Amount"
                value={item.amount}
                onChange={(e) =>
                  setItem({
                    ...item,
                    amount: e.target.value,
                  })
                }
              />

              <Input
                type="number"
                placeholder="Qty"
                value={item.quantity}
                onChange={(e) =>
                  setItem({
                    ...item,
                    quantity: e.target.value,
                  })
                }
              />

              <Button className="btn-primary" onClick={() => addItem()}>
                <Plus className="mr-2 h-4 w-4" />
                Add
              </Button>
            </CardContent>
          </Card>

          <DataTable
            title={`Billing Items (${items.length})`}
            rows={items.map((row, index) => ({
              ...row,
              id: index,
            }))}
            empty="No items added"
            columns={[
              {
                key: "name",
                label: "Item",
              },

              {
                key: "category",
                label: "Category",
              },

              {
                key: "quantity",
                label: "Qty",
              },

              {
                key: "amount",
                label: "Rate",
                align: "right",

                render: (row) => formatCurrency(row.amount),
              },

              {
                key: "total",
                label: "Total",
                align: "right",

                render: (row) => formatCurrency(row.amount * row.quantity),
              },

              {
                key: "remove",

                label: "",

                render: (row) => (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() =>
                      setItems(items.filter((_, index) => index !== row.id))
                    }
                  >
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </Button>
                ),
              },
            ]}
          />
        </div>

        <Card className="h-fit border border-gray-200">
          <CardHeader className="bg-hospital-primary text-white">
            <CardTitle className="flex items-center gap-2 text-base">
              <Receipt className="h-5 w-5" />
              Invoice Summary
            </CardTitle>
          </CardHeader>

          <CardContent className="space-y-4 p-5">
            <div className="space-y-2">
              <Label>Bill Date</Label>

              <Input
                type="date"
                value={billDate}
                onChange={(e) => setBillDate(e.target.value)}
              />
              <p className="text-xs text-hospital-text-muted">
                Purana bill entry karte time actual bill date select karein.
              </p>
            </div>

            <div className="flex justify-between">
              <span>Subtotal</span>

              <strong>{formatCurrency(subtotal)}</strong>
            </div>

            <div className="space-y-2">
              <Label>Discount</Label>

              <Input
                type="number"
                value={discount}
                onChange={(e) => setDiscount(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Paid Amount</Label>

              <Input
                type="number"
                placeholder={String(total)}
                value={paidAmount}
                onChange={(e) => setPaidAmount(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Payment Mode</Label>

              <Select value={paymentMode} onValueChange={setPaymentMode}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>

                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>

                  <SelectItem value="upi">UPI</SelectItem>

                  <SelectItem value="card">Card</SelectItem>

                  <SelectItem value="bank">Bank</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Status</Label>

              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>

                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>

                  <SelectItem value="paid">Paid</SelectItem>

                  <SelectItem value="partial">Partial</SelectItem>

                  <SelectItem value="unpaid">Unpaid</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Invoice Notes</Label>

              <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>

            <div className="rounded-lg bg-hospital-primary-light p-4">
              <div className="flex justify-between font-bold text-hospital-primary">
                <span>Total</span>

                <span>{formatCurrency(total)}</span>
              </div>
            </div>

            <Button className="btn-primary w-full py-6" onClick={submit}>
              {editingBill ? "Update Invoice" : "Generate Invoice"}
            </Button>

            {editingBill && (
              <Button variant="outline" className="w-full" onClick={resetBillForm}>
                Cancel Edit
              </Button>
            )}
          </CardContent>
        </Card>
      </div>

      <DataTable
        title={`Invoice History (${filteredBills.length})`}
        rows={filteredBills}
        empty="No invoices found"
        actions={
          <div className="grid w-full grid-cols-1 gap-2 lg:grid-cols-[minmax(260px,1fr)_180px_180px_auto]">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                className="pl-10"
                placeholder="Search patient name, mobile, patient ID, bill no..."
                value={billSearch}
                onChange={(event) => setBillSearch(event.target.value)}
              />
            </div>
            <Select value={billDateFilter} onValueChange={setBillDateFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Date filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All dates</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="week">This week</SelectItem>
                <SelectItem value="15days">Last 15 days</SelectItem>
                <SelectItem value="30days">Last 30 days</SelectItem>
              </SelectContent>
            </Select>
            <Select value={billMonthFilter} onValueChange={setBillMonthFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Month" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All months</SelectItem>
                {billMonthOptions.map((month) => (
                  <SelectItem key={month} value={month}>
                    {monthLabel(month)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              onClick={() => {
                setBillSearch("");
                setBillDateFilter("all");
                setBillMonthFilter("all");
              }}
            >
              Clear
            </Button>
          </div>
        }
        columns={billColumns}
      />

      <Dialog open={Boolean(viewingBill)} onOpenChange={(open) => !open && setViewingBill(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Invoice Details</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg bg-hospital-primary-light p-4 text-sm">
              <div className="flex justify-between"><span>Invoice</span><strong>{viewingBill?.invoice_no || viewingBill?.id?.slice(0, 8)}</strong></div>
              <div className="flex justify-between"><span>Patient</span><strong>{viewingBill?.patient_name}</strong></div>
              <div className="flex justify-between"><span>Mobile</span><strong>{viewingBill?.patient_mobile || "-"}</strong></div>
              <div className="flex justify-between"><span>Date</span><strong>{formatDate(viewingBill?.bill_date || viewingBill?.created_at)}</strong></div>
              <div className="flex justify-between"><span>Created Time</span><strong>{formatTime(viewingBill?.created_at)}</strong></div>
              <div className="flex justify-between"><span>Status</span><strong className="capitalize">{viewingBill?.status}</strong></div>
            </div>
            <DataTable
              title="Items"
              rows={(viewingBill?.items || []).map((line, index) => ({ ...line, id: index }))}
              empty="No items"
              columns={[
                { key: "name", label: "Item" },
                { key: "category", label: "Category" },
                { key: "quantity", label: "Qty" },
                { key: "rate", label: "Rate", align: "right", render: (row) => formatCurrency(row.rate || row.amount) },
                { key: "amount", label: "Total", align: "right", render: (row) => formatCurrency(row.amount) },
              ]}
            />
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div className="rounded-lg border p-3"><span className="text-hospital-text-muted">Total</span><div className="font-bold">{formatCurrency(viewingBill?.total)}</div></div>
              <div className="rounded-lg border p-3"><span className="text-hospital-text-muted">Paid</span><div className="font-bold">{formatCurrency(viewingBill?.paid_amount)}</div></div>
              <div className="rounded-lg border p-3"><span className="text-hospital-text-muted">Due</span><div className="font-bold">{formatCurrency(viewingBill?.due_amount)}</div></div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
